import React, { useMemo } from "react";
import { Skeleton } from "boneyard-js/react";
import Swipper from "./Swipper";
import { AD_SORTING, AD_TYPES } from "../utils/enums";
import { Constants } from "../utils";
import { usePersistStore } from "../store";
import { CoverSlide } from "../types";
import { useAdSearch } from "../hooks/queries";

// Snapshotted by the boneyard CLI instead of the live carousel: capturing
// the real Swiper also captures the neighboring slides in the strip (at
// x > 100%) and the over-scaled blurred backdrop, which render as stray
// bones outside the hero. One static slide, no backdrop, same CSS.
const HeroFixture: React.FC = () => (
  <div className="heroSwipperWrap heroSwipperWrap--loading">
    <div className="heroGallery__slide">
      <div className="heroGallery__stage">
        <div
          className="heroGallery__art"
          style={{ width: "56%", height: "84%" }}
        />
      </div>
      <div className="heroGallery__content">
        <span className="heroGallery__kicker">Featured</span>
        <h2 className="heroGallery__title">Featured artwork</h2>
        <div className="heroGallery__meta">Painting - Landscape - Oil</div>
        <p className="heroGallery__desc">
          A short description of the featured artwork, spanning roughly two
          lines of body copy in the info panel.
        </p>
        <button type="button" className="heroGallery__cta">
          <span>View artwork</span>
        </button>
      </div>
    </div>
  </div>
);

const RecentArtworksSwipper: React.FC = () => {
  const { currentLocation } = usePersistStore((s: any) => s);

  // Shared cache key across Home/Explore (these params never include the
  // store filters, so it's distinct from the filtered carousels).
  const { data, isLoading } = useAdSearch({
    type: AD_TYPES.ALL,
    page: 0,
    limit: 5,
    sorting: AD_SORTING.RECENT_FIRST,
    keyword: "",
    range: Constants.PRICE_RANGE,
    radius: Constants.SEARCH_MAX_RADIUS,
    mediumIds: [],
    location: currentLocation,
  });
  // Skeleton until the first response so the hero slot doesn't render an
  // empty (zero-slide) Swipper for a frame and shift the page.
  const isReady = !isLoading;

  const list: CoverSlide[] = useMemo(
    () =>
      (data?.ads || []).map((tt: any) => ({
        _id: tt._id,
        backgroundImage: tt?.images?.[0],
        title: tt?.title,
        description: tt?.description,
        description2: `${tt?.categoryId?.name} - ${tt?.subCategoryId?.name} - ${tt?.mediumId?.name}`,
      })),
    [data]
  );

  // Bones for `hero-swipper` are generated by `npx boneyard-js build`
  // — it visits the home page in headless Chromium, snapshots the
  // rendered Swipper, and writes the descriptor into ./src/bones.
  //
  // The .heroSwipperWrap class gives an aspect-ratio per breakpoint
  // matching the captured bones. An empty Swipper renders 0px tall,
  // which would otherwise collapse the parent and leave boneyard's
  // bones — absolute-positioned inside the wrapper — with nothing to
  // overlay (or scale them to the wrong height). Once slides arrive,
  // the natural slide content height pushes past the aspect-ratio
  // floor and takes over.
  return (
    <Skeleton name="hero-swipper" loading={!isReady} fixture={<HeroFixture />}>
      <div
        className={
          "heroSwipperWrap" + (isReady ? "" : " heroSwipperWrap--loading")
        }
      >
        <Swipper slidesData={list} />
      </div>
    </Skeleton>
  );
};

export default RecentArtworksSwipper;
